// Business-hours domain for the per-location Location settings page, plus the after-hours auto
// sign-out interval default. Pure (no Ember services) so it's unit-testable on its own.
//
// Per-day start/end are held as "HH:mm:ss" strings — the value format of the hour-options dropdowns
// (mirroring the Desk "Booking hours" panel) — and converted to/from the API's "HH:mm" at the edges.
import { tracked } from '@glimmer/tracking';
import { endTimeOptions, startTimeOptions } from 'garaje/utils/hour-options';

// Uppercase weekday keys (match the backend contract), ordered Sunday→Saturday for display (matches
// the Booking hours weekday selector).
export const WEEKDAYS = [
  { key: 'SUNDAY', label: 'Sunday' },
  { key: 'MONDAY', label: 'Monday' },
  { key: 'TUESDAY', label: 'Tuesday' },
  { key: 'WEDNESDAY', label: 'Wednesday' },
  { key: 'THURSDAY', label: 'Thursday' },
  { key: 'FRIDAY', label: 'Friday' },
  { key: 'SATURDAY', label: 'Saturday' },
] as const;

// After-hours auto sign-out interval default (minutes). Matches the server default. Lives with
// business hours because the interval only applies once a business-hours window is configured.
export const AFTER_HOURS_ABSENCE_MINUTES_DEFAULT = 60;

// Default window applied when a weekday is newly opened (all-day, 12am–11:59pm). "HH:mm:ss" to match
// the dropdown option values ("23:59:00" is the special end-of-day option).
export const DEFAULT_BUSINESS_HOURS_START = '00:00:00';
export const DEFAULT_BUSINESS_HOURS_END = '23:59:00';

// Minutes between dropdown time options (matches the Booking hours panel).
const TIME_OPTION_INTERVAL_MINUTES = 15;

/** A start/end dropdown option from hour-options (e.g. { value: '09:00:00', label: '9:00 am' }). */
export interface HourOption {
  value: string;
  label: string;
}

/** API-facing per-day window: closed days carry no times. Times are 24-hour "HH:mm". */
export interface DayWindowPayload {
  enabled: boolean;
  start?: string;
  end?: string;
}

/** The full business-hours block in API shape (held by the persisted snapshot and the draft getter). */
export interface BusinessHoursPayload {
  enabled: boolean;
  // After-hours presence report opt-in. Lives in the business-hours block (sibling of `enabled`);
  // forced false whenever business hours is disabled so we never persist an opt-in that can't fire.
  'after-hours-report-enabled': boolean;
  days: Record<string, DayWindowPayload>;
}

// The API stores 24-hour "HH:mm"; the dropdown option values are "HH:mm:ss". Convert at the edges.
function toOptionValue(apiTime: string): string {
  return apiTime.length >= 8 ? apiTime : `${apiTime}:00`;
}
function toApiValue(optionValue: string): string {
  return optionValue.slice(0, 5);
}

/**
 * Draft state for one weekday's business hours. `start`/`end` are "HH:mm:ss" (the hour-options
 * dropdown value format); the dropdown option lists + the selected option are exposed as getters so
 * the template is a straight binding. Tracked so per-day edits drive the Save gating.
 */
export class BusinessHoursDayDraft {
  readonly key: string;
  readonly label: string;
  @tracked enabled: boolean;
  @tracked start: string | null;
  @tracked end: string | null;

  constructor(key: string, label: string, enabled: boolean, start: string | null, end: string | null) {
    this.key = key;
    this.label = label;
    this.enabled = enabled;
    this.start = start;
    this.end = end;
  }

  // First letter, for the compact weekday selector button (e.g. "M" for Monday).
  get initial(): string {
    return this.label.charAt(0);
  }

  // Start options: 15-min slots strictly before the chosen end (so an out-of-order range can't be
  // picked). End options: slots strictly after the chosen start. Mirrors the Booking hours panel.
  get startOptions(): HourOption[] {
    return startTimeOptions(this.end ?? '23:59:00', TIME_OPTION_INTERVAL_MINUTES, true) as HourOption[];
  }

  get endOptions(): HourOption[] {
    return endTimeOptions(this.start ?? '00:00:00', TIME_OPTION_INTERVAL_MINUTES, true) as HourOption[];
  }

  get selectedStartOption(): HourOption | undefined {
    return this.startOptions.find((option) => option.value === this.start);
  }

  get selectedEndOption(): HourOption | undefined {
    return this.endOptions.find((option) => option.value === this.end);
  }

  // Both times set and start strictly before end (lexicographic compare is chronological for the
  // zero-padded "HH:mm:ss" format).
  get rangeValid(): boolean {
    if (!this.start || !this.end) return false;
    return this.start < this.end;
  }

  // Both set but out of order — kept as a save-time safety net (the filtered dropdowns prevent it).
  get outOfOrder(): boolean {
    if (!this.start || !this.end) return false;
    return this.start >= this.end;
  }

  // A saveable state: closed (no window) or open with a complete, ordered window.
  get valid(): boolean {
    return !this.enabled || this.rangeValid;
  }

  // The window in API shape ("HH:mm"); only valid to read when `valid` (enabled days must have times).
  get apiWindow(): DayWindowPayload {
    if (!this.enabled) return { enabled: false };
    return {
      enabled: true,
      start: toApiValue(this.start!),
      end: toApiValue(this.end!),
    };
  }
}

// Build the 7 per-day drafts from an API business-hours block. Always returns all weekdays in display
// order so the selector is complete.
//
// When there's no business-hours block at all (an unconfigured location), default to every day open
// 12am–11:59pm — an editable, intuitive starting point ("open 24/7" ≈ "no after-hours restriction",
// the same practical meaning as unconfigured). A real config (even one with days turned off) drives
// the per-day state instead, so a deliberately-closed day stays closed.
export function buildBusinessHoursDays(
  businessHours?: { days?: Record<string, DayWindowPayload> } | null,
): BusinessHoursDayDraft[] {
  if (!businessHours) {
    return WEEKDAYS.map(
      ({ key, label }) =>
        new BusinessHoursDayDraft(key, label, true, DEFAULT_BUSINESS_HOURS_START, DEFAULT_BUSINESS_HOURS_END),
    );
  }
  return WEEKDAYS.map(({ key, label }) => {
    const window = businessHours.days?.[key];
    if (window?.enabled && window.start && window.end) {
      return new BusinessHoursDayDraft(key, label, true, toOptionValue(window.start), toOptionValue(window.end));
    }
    return new BusinessHoursDayDraft(key, label, false, null, null);
  });
}

// Normalize a business-hours payload to a stable, order-independent representation for the dirty
// diff: every weekday in fixed order, with missing/closed days collapsed to `{ enabled: false }`.
export function canonicalizeBusinessHours(payload: BusinessHoursPayload): {
  enabled: boolean;
  'after-hours-report-enabled': boolean;
  days: Array<[string, DayWindowPayload]>;
} {
  const days = WEEKDAYS.map(({ key }): [string, DayWindowPayload] => {
    const window = payload.days[key];
    if (!window || !window.enabled) return [key, { enabled: false }];
    return [key, { enabled: true, start: window.start, end: window.end }];
  });
  return {
    enabled: payload.enabled,
    'after-hours-report-enabled': payload['after-hours-report-enabled'],
    days,
  };
}
